import { Collection } from 'firestorter';

import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

/**
 * Where a deploy is: the first three are in progress, the last two final. Mirrors
 * WebflowDeployStatus in functions-22/src/db/types/webflow-deploy.ts.
 */
const WebflowDeployStatus = {
  PREPARING: 'PREPARING',
  UPLOADING: 'UPLOADING',
  FINALIZING: 'FINALIZING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/**
 * Statuses of a deploy still running. IN_PROGRESS is the status the first dev deploys were
 * written with, before the stage merged into it; all of those have long finished one way or the
 * other, so it is only here to read them correctly.
 */
const IN_PROGRESS_STATUSES = [
  WebflowDeployStatus.PREPARING,
  WebflowDeployStatus.UPLOADING,
  WebflowDeployStatus.FINALIZING,
  'IN_PROGRESS',
];

const HISTORY_LIMIT = 20;

/**
 * One Webflow export deployed to Netlify, as recorded by the deployWebflowSite endpoint.
 *
 * Read-only here — the endpoint owns the document from creation to terminal status — and
 * observed rather than fetched, so the page follows a deploy's progress as the server writes
 * it. Same model as MigrationRun.
 */
class WebflowDeploy extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.WEBFLOW_DEPLOYS}/${id}`, opts);
  }

  get status() {
    return this.data.status;
  }

  get uploaded() {
    return this.data.uploaded || 0;
  }

  get total() {
    return this.data.total || 0;
  }

  get fileName() {
    return this.data.fileName || '';
  }

  get startedBy() {
    return this.data.startedBy || '';
  }

  /** The operator's name as stored when the run started; runs written before it was stored lack it. */
  get startedByName() {
    return this.data.startedByName || '';
  }

  get netlifySiteName() {
    return this.data.netlifySiteName || '';
  }

  get url() {
    return this.data.url || '';
  }

  get warnings() {
    return this.data.warnings || [];
  }

  get error() {
    return this.data.error || '';
  }

  get createdAt() {
    return this.data.createdAt?.toDate();
  }

  /**
   * When the endpoint's own timeout expires. A deploy the platform kills never writes a
   * terminal status, so past this an open deploy is one that died rather than one still
   * working.
   *
   * @returns {Date|undefined} - The deploy's deadline.
   */
  get deadlineAt() {
    return this.data.deadlineAt?.toDate();
  }

  /**
   * An unused deploy doc id, so the page can name the upload and start watching the deploy
   * before the request that creates it has answered. See BaseDocument.reserveId.
   *
   * @returns {string} - An unused webflowDeploys doc id.
   */
  static reserveRunId() {
    return BaseDocument.reserveId(Collections.WEBFLOW_DEPLOYS);
  }

  /**
   * The most recent deploys, newest first. Not fetched here: the collection listens while a
   * mobx observer renders its docs, so history picks up deploys started from other tabs too.
   *
   * @param {number} [limit] - How many deploys to list.
   * @returns {Collection} - The observable collection.
   */
  static recent(limit = HISTORY_LIMIT) {
    return new Collection(Collections.WEBFLOW_DEPLOYS, {
      createDocument: (source, opts) => new WebflowDeploy(source.id, opts),
      query: (ref) => ref.orderBy('createdAt', 'desc').limit(limit),
    });
  }
}

export default WebflowDeploy;
export {
  WebflowDeployStatus,
  IN_PROGRESS_STATUSES,
};
